import { Box, List, ListItemButton, Paper, Typography } from "@mui/material";
import { orange } from "@mui/material/colors";
import { alpha } from "@mui/material/styles";
import { makeModeAwareAccent } from "@entrilia/shared/utilities/colorHelper";
import type { FormulaSuggestion } from "./utilities/customFormulaSuggestions";

interface Props {
  open: boolean;
  suggestions: FormulaSuggestion[];
  query: string;
  selectedIndex: number;
  onSelectedIndexChange: (value: number) => void;
  onSuggestionSelect: (suggestion: FormulaSuggestion) => void;
}

const getSuggestionTypeLabel = (kind: FormulaSuggestion["kind"]): string => {
  if (kind === "function") {
    return "Function";
  }

  return "Column";
};

interface HighlightedPart {
  value: string;
  highlighted: boolean;
}

const splitHighlightedLabel = (label: string, query: string): HighlightedPart[] => {
  if (query.length === 0) {
    return [{ value: label, highlighted: false }];
  }

  const normalizedLabel = label.toLowerCase();
  const normalizedQuery = query.toLowerCase();
  const parts: HighlightedPart[] = [];
  let searchFrom = 0;

  while (searchFrom < label.length) {
    const matchIndex = normalizedLabel.indexOf(normalizedQuery, searchFrom);
    if (matchIndex < 0) {
      parts.push({ value: label.slice(searchFrom), highlighted: false });
      break;
    }

    if (matchIndex > searchFrom) {
      parts.push({ value: label.slice(searchFrom, matchIndex), highlighted: false });
    }

    parts.push({
      value: label.slice(matchIndex, matchIndex + query.length),
      highlighted: true,
    });
    searchFrom = matchIndex + query.length;
  }

  return parts;
};

const SuggestionLabel = ({ label, query }: { label: string; query: string }) => {
  const highlightedLabelParts = splitHighlightedLabel(label, query);

  return (
    <Typography variant="body2" noWrap>
      {highlightedLabelParts.map((part, index) => {
        if (!part.highlighted) {
          return (
            // eslint-disable-next-line @eslint-react/no-array-index-key
            <Typography variant="caption" component="span" key={`${part.value}-${index}`}>
              {part.value}
            </Typography>
          );
        }

        return (
          <Typography
            variant="caption"
            component="span"
            // eslint-disable-next-line @eslint-react/no-array-index-key
            key={`${part.value}-${index}`}
            sx={(t) => ({ bgcolor: alpha(makeModeAwareAccent(t, orange), 0.3), borderRadius: "2px", px: 0.2 })}
          >
            {part.value}
          </Typography>
        );
      })}
    </Typography>
  );
};

const CustomFormulaSuggestionsPopup = ({
  open,
  suggestions,
  query,
  selectedIndex,
  onSelectedIndexChange,
  onSuggestionSelect,
}: Props) => {
  if (!open || suggestions.length === 0) {
    return null;
  }

  return (
    <Paper
      sx={(theme) => ({
        position: "absolute",
        left: 0,
        top: "calc(100% + 6px)",
        width: 360,
        maxWidth: "100%",
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.shadows[2],
        zIndex: 2,
        overflow: "hidden",
      })}
    >
      <List disablePadding sx={{ maxHeight: 208, overflowY: "auto" }}>
        {suggestions.map((suggestion, index) => (
          <ListItemButton
            key={suggestion.key}
            selected={selectedIndex === index}
            onMouseEnter={() => onSelectedIndexChange(index)}
            onMouseDown={(event) => {
              event.preventDefault();
              onSuggestionSelect(suggestion);
            }}
            sx={{
              px: 1.25,
              py: 0.75,
              justifyContent: "space-between",
              alignItems: "center",
              gap: 1,
              "&.Mui-selected": { bgcolor: alpha("#00AA5E", 0.08) },
              "&.Mui-selected:hover": { bgcolor: alpha("#00AA5E", 0.08) },
            }}
          >
            <SuggestionLabel label={suggestion.label} query={query} />
            <Box sx={{ flexShrink: 0 }}>
              <Typography variant="caption" color="textSecondary">
                {getSuggestionTypeLabel(suggestion.kind)}
              </Typography>
            </Box>
          </ListItemButton>
        ))}
      </List>
    </Paper>
  );
};

export default CustomFormulaSuggestionsPopup;
